import * as log from '../../util/log';
import { syncSoldOutFlag } from '../../util/soldOutSync';
import { delay, withRetryOn429 } from '../../util/sdkRetry';
import { getListingImageVariants } from '../../util/listingImageVariants';

// ================ Action types ================ //

const IMPORT_REQUEST = 'app/BulkImportListingsPage/IMPORT_REQUEST';
const IMPORT_ROW_SUCCESS = 'app/BulkImportListingsPage/IMPORT_ROW_SUCCESS';
const IMPORT_ROW_ERROR = 'app/BulkImportListingsPage/IMPORT_ROW_ERROR';
const IMPORT_COMPLETE = 'app/BulkImportListingsPage/IMPORT_COMPLETE';
const IMPORT_RESET = 'app/BulkImportListingsPage/IMPORT_RESET';

// ================ Reducer ================ //

const initialState = {
  importInProgress: false,
  currentIndex: 0,
  totalCount: 0,
  results: [],
};

export default function reducer(state = initialState, action = {}) {
  const { type, payload } = action;

  switch (type) {
    case IMPORT_REQUEST:
      return {
        ...state,
        importInProgress: true,
        currentIndex: 0,
        totalCount: payload.totalCount,
        results: Array(payload.totalCount)
          .fill(null)
          .map((_, i) => ({ rowIndex: i, status: 'pending', listingId: null, error: null })),
      };

    case IMPORT_ROW_SUCCESS:
      return {
        ...state,
        currentIndex: payload.index + 1,
        results: state.results.map((r, i) =>
          i === payload.index
            ? { ...r, status: 'success', listingId: payload.listingId }
            : r
        ),
      };

    case IMPORT_ROW_ERROR:
      return {
        ...state,
        currentIndex: payload.index + 1,
        results: state.results.map((r, i) =>
          i === payload.index
            ? { ...r, status: 'error', error: payload.error }
            : r
        ),
      };

    case IMPORT_COMPLETE:
      return {
        ...state,
        importInProgress: false,
      };

    case IMPORT_RESET:
      return initialState;

    default:
      return state;
  }
}

// ================ Action creators ================ //

const importRequest = totalCount => ({ type: IMPORT_REQUEST, payload: { totalCount } });
const importRowSuccess = (index, listingId) => ({
  type: IMPORT_ROW_SUCCESS,
  payload: { index, listingId },
});
const importRowError = (index, error) => ({
  type: IMPORT_ROW_ERROR,
  payload: { index, error },
});
const importComplete = () => ({ type: IMPORT_COMPLETE });
export const importReset = () => ({ type: IMPORT_RESET });

// ================ Helpers ================ //

/**
 * Compare and set stock for a listing.
 */
const setStock = async (sdk, listingId, oldTotal, newTotal) => {
  if (newTotal >= 0 && typeof oldTotal !== 'undefined') {
    await sdk.stock.compareAndSet(
      { listingId, oldTotal, newTotal },
      { expand: true }
    );
  }
};

// ================ Thunks ================ //

/**
 * Import listings sequentially.
 * @param {Array<Object>} validRows - Array of { row, listingData, imageFiles }
 * @param {Object} config - App config for image variants
 */
export const importListings = (validRows, config) => {
  return async (dispatch, getState, sdk) => {
    dispatch(importRequest(validRows.length));

    const listingImageConfig = config?.layout?.listingImage || {};
    const imageVariants = getListingImageVariants(listingImageConfig);
    const imageQueryParams = { expand: true, ...imageVariants };
    const listingQueryParams = {
      expand: true,
      include: ['author', 'images', 'currentStock'],
      ...imageVariants,
    };

    for (let i = 0; i < validRows.length; i++) {
      const { listingData, imageFiles } = validRows[i];

      try {
        // 1. Upload images in parallel
        const imageUUIDs = await Promise.all(
          imageFiles.map(async file => {
            const resp = await withRetryOn429(() =>
              sdk.images.upload({ image: file }, imageQueryParams)
            );
            return resp.data.data.id;
          })
        );

        // 2. Create draft listing
        const { stockUpdate, images: _, ...rest } = listingData;
        const listingValues = {
          ...rest,
          images: imageUUIDs,
        };

        const createDraftResponse = await withRetryOn429(() =>
          sdk.ownListings.createDraft(listingValues, listingQueryParams)
        );

        const listingId = createDraftResponse.data.data.id;

        // 3. Set stock
        if (stockUpdate) {
          await setStock(sdk, listingId, stockUpdate.oldTotal, stockUpdate.newTotal);
          // Sync soldOut flag — best-effort, don't fail the import if this fails
          await syncSoldOutFlag(sdk, listingId, stockUpdate.newTotal).catch(() => {});
        }

        dispatch(importRowSuccess(i, listingId));
      } catch (e) {
        log.error(e, 'bulk-import-row-failed', { rowIndex: i });
        // Without a message the page says the row failed (BulkImportListingsPage.resultFailed)
        const errorMessage = e?.data?.errors?.[0]?.title || e?.message || null;
        dispatch(importRowError(i, errorMessage));
      }

      // Delay between listings to avoid rate limits
      if (i < validRows.length - 1) {
        await delay(150);
      }
    }

    dispatch(importComplete());
  };
};
